import {
  COMPANY_MANAGEMENT_SERVICE,
  EMPLOYEE_MANAGEMENT_SERVICE,
  MASTERDATA_SERVICE,
  API_GATEWAY_SERVICE,
  restApiRequest,
  OPERATOR_MANAGEMENT_SERVICE,
} from "src/utils/Api";
import { mockData as companyMockData } from "src/Pages/Company/CompanyList";
import { mockData as employeesMockData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/List/list";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from "src/utils/constants/api";

/** METHODS FOR ASYNC AUTOCOMPLETE */

export const getCompaniesOptionsFetchMethod =
  (currValue, customItemsCountPerRequest, filterKey = "fullName") =>
  async (inputValue = "", page = 1) => {
    const params = {
      page,
      itemsPerPage:
        customItemsCountPerRequest || DEFAULT_ITEMS_COUNT_PER_REQUEST,
    };
    let mockData = [];
    if (inputValue) {
      params[filterKey] = inputValue;
      mockData = companyMockData.filter((company) =>
        company.fullName.toLowerCase().includes(inputValue.toLowerCase())
      );
    } else if (currValue && !Array.isArray(currValue)) {
      params.id = currValue;
      mockData = companyMockData.filter((company) => company.id === currValue);
    } else if (Array.isArray(currValue) && currValue.length) {
      params.id = currValue;
      mockData = companyMockData.filter((company) =>
        currValue.includes(company.id)
      );
    }
    return restApiRequest(
      COMPANY_MANAGEMENT_SERVICE,
      "/companies-names",
      "GET",
      { params },
      mockData
    )
      .then((result) =>
        result.map(({ id, ...rest }) => ({
          value: id,
          label: rest[filterKey],
        }))
      )
      .catch((e) => {
        newRelicErrorReport(
          e,
          "Components/FormElements/Autocomplete/commonFetchMethods.js - 47"
        );
      });
  };

export const getPrepaidCardOptionsFetchMethod =
  (currentValue = null) =>
  () =>
    restApiRequest(
      API_GATEWAY_SERVICE,
      "/admin-prepaid-cards/prepaid-cards",
      "GET",
      {}
    )
      .then((result) => {
        const response = result?.prepaidCards?.map(({ id, name }) => ({
          value: id,
          label: name,
        }));

        if (currentValue)
          return response.filter(({ value }) => value === currentValue);

        return response;
      })
      .catch((e) => {
        console.error(e);
        newRelicErrorReport(
          e,
          "Components/FormElements/Autocomplete/commonFetchMethods.js - 70"
        );
        return [];
      });

export const getEmployeesOptionsFetchMethod =
  (currValue, additionalFilterParams, withPagination = true) =>
  async (inputValue = "", page = 1) => {
    const params = withPagination
      ? {
          page,
          itemsPerPage: DEFAULT_ITEMS_COUNT_PER_REQUEST,
        }
      : {};

    let mockData = [];
    if (inputValue) {
      params.or_searchByName = [inputValue];
      mockData = employeesMockData.filter((employee) =>
        employee.firstName.toLowerCase().includes(inputValue.toLowerCase())
      );
    } else if (currValue && !Array.isArray(currValue)) {
      params.id = currValue;
      mockData = employeesMockData.filter(
        (employee) => employee.id === currValue
      );
    } else if (Array.isArray(currValue) && currValue.length) {
      params.id = currValue;
      mockData = employeesMockData.filter((employee) =>
        currValue.includes(employee.id)
      );
    }
    const fetchByName = restApiRequest(
      EMPLOYEE_MANAGEMENT_SERVICE,
      "/employees",
      "GET",
      {
        params: { ...params, ...additionalFilterParams },
      },
      mockData
    )
      .then((result) =>
        result.map(({ id, firstName, lastName, fk }) => ({
          value: id,
          label: `${firstName} ${lastName}${fk ? `(fk: ${fk})` : ""}`,
        }))
      )
      .catch((e) => {
        newRelicErrorReport(
          e,
          "Components/FormElements/Autocomplete/commonFetchMethods.js - 112"
        );
      });
    const promises = [fetchByName];

    if (inputValue) {
      const fetchByFk = restApiRequest(
        EMPLOYEE_MANAGEMENT_SERVICE,
        "/employees",
        "GET",
        {
          params: {
            fk: inputValue,
            ...(withPagination
              ? {
                  page,
                  itemsPerPage: DEFAULT_ITEMS_COUNT_PER_REQUEST,
                }
              : {}),
            ...additionalFilterParams,
          },
        },
        mockData
      )
        .then((result) =>
          result.map(({ id, firstName, lastName, fk = "" }) => ({
            value: id,
            label: `${firstName} ${lastName}${fk ? `(fk: ${fk})` : ""}`,
          }))
        )
        .catch((e) => {
          newRelicErrorReport(
            e,
            "Components/FormElements/Autocomplete/commonFetchMethods.js - 134"
          );
        });
      promises.push(fetchByFk);
    }

    const responses = await Promise.all(promises);

    let result = [];
    responses.forEach((requestResult) => {
      result = result.concat(requestResult);
    });
    return result.filter(
      (value, index, self) =>
        self.findIndex((el) => el.value === value.value) === index
    );
  };

const fetch = async (params) => {
  const { totalItems } =
    (await restApiRequest(
      MASTERDATA_SERVICE,
      "/supplierobject/nontouristic",
      "GET",
      {
        params: {
          rowsPerPage: 1,
        },
      },
      {}
    )) || {};

  const batches = Array.from(
    { length: Math.ceil(totalItems / 3000) },
    (_, i) => i + 1
  );

  try {
    const res = await Promise.all(
      batches.map((batch) =>
        restApiRequest(
          MASTERDATA_SERVICE,
          "/supplierobject/nontouristic",
          "GET",
          {
            params: {
              rowsPerPage: 3000,
              page: batch,
            },
          },
          {}
        )
      )
    );

    const items = [];
    res?.forEach(({ member }) =>
      items.push(
        member
          .map(({ supplierObjectBusinessID, objectName }) =>
            supplierObjectBusinessID
              ? {
                  value: supplierObjectBusinessID.toLowerCase(),
                  label: objectName,
                }
              : null
          )
          .filter(Boolean)
      )
    );

    return items.flat();
  } catch (e) {
    console.error(e);
    newRelicErrorReport(
      e,
      "Components/FormElements/Autocomplete/commonFetchMethods.js - 180"
    );
    return [];
  }
};

let data;

export const getSuppliersOptionsFetchMethod =
  (currValue) =>
  async (inputValue = "") => {
    if (!data) {
      data = await fetch();
    }

    if (inputValue) {
      return data.filter(
        (el) =>
          el.value?.toLowerCase() === inputValue?.toLowerCase() ||
          el.label.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    if (currValue) {
      return data.filter(
        (el) => el.value?.toLowerCase() === currValue?.toLowerCase()
      );
    }
    return [];
  };

export const getOperatorsOptionsFetchMethod =
  (currValue, customItemsCountPerRequest) =>
  async (inputValue = "", page = 1) => {
    const params = {
      page,
      itemsPerPage:
        customItemsCountPerRequest || DEFAULT_ITEMS_COUNT_PER_REQUEST,
    };
    const mockData = [];
    if (inputValue) {
      params.username = inputValue;
    } else if (currValue && !Array.isArray(currValue)) {
      params.id = currValue;
    } else if (Array.isArray(currValue) && currValue.length) {
      params.id = currValue;
    }
    return restApiRequest(
      OPERATOR_MANAGEMENT_SERVICE,
      "/operators",
      "GET",
      { params },
      mockData
    )
      .then((result) =>
        result.map(({ id, firstName, lastName, username }) => ({
          value: id,
          label: `${firstName} ${lastName}${
            username ? `(username: ${username})` : ""
          }`,
        }))
      )
      .catch((e) => {
        newRelicErrorReport(
          e,
          "Components/FormElements/Autocomplete/commonFetchMethods.js - 260"
        );
      });
  };
